import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import EventEmitter, { EVENT_TYPES } from '@lib/eventEmitter';

const EnquiryParams = ({
  authenticityToken,
  clickAndCollectAvailable,
  deliveryPreferences,
  enquiry,
  enquiryPath,
  finance,
}) => {
  const [params, setParams] = useState({
    enquiry: enquiry,
    financeCalculated: false,
    submitForm: false,
  });

  const formRef = useRef(null);

  const hasIllustrationUpdated = (illustration) =>
    Object.entries(illustration).toString() !==
    Object.entries(enquiry.finance).toString();

  const updateIllustration = (illustration) => {
    setParams({
      enquiry: { ...enquiry, finance: illustration, interestedInFinance: true },
      financeCalculated: hasIllustrationUpdated(illustration),
      submitForm: true,
    });
  };

  const isFinanceCalculated = () => {
    if (!finance?.selectedFinance) return false;
    const selectedFinance = finance.selectedFinance;
    return Object.prototype.hasOwnProperty.call(
      finance[selectedFinance],
      'calculator',
    );
  };

  const getValueFromString = (string) =>
    parseFloat(string.replace(/[£,]/g, ''));

  const submitEnquiryForm = () => formRef.current && formRef.current.submit();

  useEffect(() => {
    EventEmitter.on(EVENT_TYPES.submitEnquiryForm, submitEnquiryForm);
    EventEmitter.on(EVENT_TYPES.submittedFinance, updateIllustration);

    return () => {
      EventEmitter.off(EVENT_TYPES.submitEnquiryForm);
      EventEmitter.off(EVENT_TYPES.submittedFinance);
    };
  }, []);

  useEffect(() => {
    params.submitForm && submitEnquiryForm();
  }, [params]);

  return (
    <form action={enquiryPath} id="enquiry_params" method="POST" ref={formRef}>
      <input
        name="authenticity_token"
        type="hidden"
        value={authenticityToken}
      />
      <input
        name="enquiry[interested_in_finance]"
        type="hidden"
        value={params.enquiry.interestedInFinance}
      />
      <input
        name="enquiry[enquiry_finance_attributes][calculated]"
        type="hidden"
        value={params.financeCalculated || isFinanceCalculated()}
      />
      {params.enquiry.finance['Description'] && (
        <input
          name="enquiry[enquiry_finance_attributes][description]"
          type="hidden"
          value={params.enquiry.finance['Description']}
        />
      )}
      {params.enquiry.finance['Monthly payment'] && (
        <input
          name="enquiry[enquiry_finance_attributes][monthly_payment]"
          type="hidden"
          value={params.enquiry.finance['Monthly payment']}
        />
      )}
      {(params.enquiry.finance['Deposit'] ||
        params.enquiry.finance['Deposit'] === 0) && (
        <input
          name="enquiry[enquiry_finance_attributes][deposit]"
          type="hidden"
          value={params.enquiry.finance['Deposit']}
        />
      )}
      {params.enquiry.finance['Term (months)'] && (
        <input
          name="enquiry[enquiry_finance_attributes][term]"
          type="hidden"
          value={params.enquiry.finance['Term (months)']}
        />
      )}
      {params.enquiry.finance['Annual mileage'] && (
        <input
          name="enquiry[enquiry_finance_attributes][annual_mileage]"
          type="hidden"
          value={getValueFromString(params.enquiry.finance['Annual mileage'])}
        />
      )}
      {params.enquiry.finance['Guaranteed future value'] && (
        <input
          name="enquiry[enquiry_finance_attributes][guaranteed_future_value]"
          type="hidden"
          value={params.enquiry.finance['Guaranteed future value']}
        />
      )}
      {params.enquiry.finance['Completion fee'] && (
        <input
          name="enquiry[enquiry_finance_attributes][completion_fee]"
          type="hidden"
          value={params.enquiry.finance['Completion fee']}
        />
      )}
      {params.enquiry.finance['Fixed interest rate'] && (
        <input
          name="enquiry[enquiry_finance_attributes][fixed_interest_rate]"
          type="hidden"
          value={params.enquiry.finance['Fixed interest rate']}
        />
      )}
      {params.enquiry.finance['apr'] && (
        <input
          name="enquiry[enquiry_finance_attributes][apr]"
          type="hidden"
          value={params.enquiry.finance['apr']}
        />
      )}
      {params.enquiry.finance['Excess mileage charge'] && (
        <input
          name="enquiry[enquiry_finance_attributes][excess_mileage_charge]"
          type="hidden"
          value={params.enquiry.finance['Excess mileage charge']}
        />
      )}
      {clickAndCollectAvailable && deliveryPreferences && (
        <input
          name="enquiry[collection_point]"
          type="hidden"
          value={
            deliveryPreferences.preference === 'HOME_DELIVERY'
              ? deliveryPreferences.preference
              : deliveryPreferences.dmsid
          }
        />
      )}
    </form>
  );
};

EnquiryParams.propTypes = {
  authenticityToken: PropTypes.string,
  clickAndCollectAvailable: PropTypes.bool,
  deliveryPreferences: PropTypes.object,
  enquiry: PropTypes.object,
  enquiryPath: PropTypes.string,
  finance: PropTypes.object,
};

export default EnquiryParams;
